import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { 
  ArrowLeft, Plus, Search, Edit, Trash2, Users, 
  Wifi, Monitor, Coffee, Car, Building,
  CheckCircle, XCircle, Eye, Home, BarChart3, Settings, 
  Bell, ChevronRight, Calendar, DollarSign, RefreshCw, 
  CreditCard, Clock, FileText, TrendingUp
} from 'lucide-react';
import { SalaService } from '@/services/salaService';
import { Sala, CreateSalaRequest, UpdateSalaRequest } from '@/types/sala';
import { toast } from 'sonner';
import ResponsiveAdminLayout from '@/components/admin/ResponsiveAdminLayout';
import { ResponsiveMetricsGrid } from '@/components/ui/responsive-charts';
import { ResponsiveForm, TouchInput } from '@/components/ui/responsive-form';
import { useResponsive } from '@/hooks/useResponsive';

const salaSchema = z.object({
  nome: z.string().min(2, 'Nome deve ter pelo menos 2 caracteres'),
  descricao: z.string().optional(),
  capacidade: z.number().min(1, 'Capacidade deve ser pelo menos 1'),
  equipamentos: z.string().optional(),
  imagem_url: z.string().url('URL inválida').optional().or(z.literal('')),
  is_active: z.boolean().default(true)
});

type SalaFormData = z.infer<typeof salaSchema>;

const AdminRooms = () => {
  const [salas, setSalas] = useState<Sala[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingSala, setEditingSala] = useState<Sala | null>(null);
  const [viewingSala, setViewingSala] = useState<Sala | null>(null);
  const { isMobile } = useResponsive();

  // Métricas das salas
  const metrics = [
    {
      title: "Total de Salas",
      value: salas.length.toString(),
      change: `${salas.filter(s => s.is_active).length} ativas`,
      bg: "bg-gradient-to-r from-blue-500 to-cyan-500",
      icon: Building
    },
    {
      title: "Salas Ativas",
      value: salas.filter(s => s.is_active).length.toString(),
      change: `${Math.round((salas.filter(s => s.is_active).length / Math.max(salas.length, 1)) * 100)}% do total`,
      bg: "bg-gradient-to-r from-green-500 to-emerald-500",
      icon: CheckCircle
    },
    {
      title: "Capacidade Total",
      value: salas.reduce((acc, sala) => acc + sala.capacidade, 0).toString(),
      change: `${Math.round(salas.reduce((acc, sala) => acc + sala.capacidade, 0) / Math.max(salas.length, 1))} média por sala`,
      bg: "bg-gradient-to-r from-purple-500 to-pink-500",
      icon: Users
    },
    {
      title: "Taxa de Ocupação",
      value: "85%",
      change: "+5% vs mês anterior",
      bg: "bg-gradient-to-r from-orange-500 to-red-500",
      icon: TrendingUp
    }
  ];

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    control,
    formState: { errors, isSubmitting }
  } = useForm<SalaFormData>({
    resolver: zodResolver(salaSchema),
    defaultValues: {
      is_active: true
    }
  });

  const loadSalas = React.useCallback(async () => {
    try {
      setLoading(true);
      // ✅ NOVA LÓGICA: Sempre filtrar salas com [REMOVIDA] no nome
      const response = await SalaService.getSalas(1, 50, searchTerm, showActiveOnly ? true : undefined);
      
      // Filtrar salas removidas (que contêm [REMOVIDA] no nome)
      const salasFiltered = response.salas.filter(sala => {
        // Se showActiveOnly está ligado, mostrar apenas ativas (exclui removidas automaticamente)
        if (showActiveOnly) {
          return sala.is_active && !(sala.nome ?? '').includes('[REMOVIDA]');
        }
        // Se showActiveOnly está desligado, mostrar todas EXCETO as removidas
        return !(sala.nome ?? '').includes('[REMOVIDA]');
      });
      
      setSalas(salasFiltered);
      
    } catch (error: unknown) {
      console.error('Erro ao carregar salas:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao carregar salas';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, showActiveOnly]);

  useEffect(() => {
    loadSalas();
  }, [loadSalas]);

  const onSubmit = async (data: SalaFormData) => {
    try {
      const equipamentos = data.equipamentos 
        ? data.equipamentos.split(',').map(eq => eq.trim()).filter(eq => eq.length > 0)
        : [];

      const salaData = {
        ...data,
        equipamentos,
        imagem_url: data.imagem_url || undefined
      };

      if (editingSala) {
        await SalaService.updateSala(editingSala.id, salaData);
        toast.success('Sala atualizada com sucesso!');
        setEditingSala(null);
      } else {
        await SalaService.createSala(salaData as CreateSalaRequest);
        toast.success('Sala criada com sucesso!');
        setIsCreateDialogOpen(false);
      }

      reset();
      loadSalas();
    } catch (error: unknown) {
      console.error('Erro ao salvar sala:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao salvar sala';
      toast.error(message);
    }
  };

  const handleEdit = (sala: Sala) => {
    setEditingSala(sala);
    setValue('nome', sala.nome);
    setValue('descricao', sala.descricao || '');
    setValue('capacidade', sala.capacidade);
    setValue('equipamentos', sala.equipamentos.join(', '));
    setValue('imagem_url', sala.imagem_url || '');
    setValue('is_active', sala.is_active);
  };

  const handleDelete = async (id: string) => {
    try {
      await SalaService.deleteSala(id);
      toast.success('Sala excluída com sucesso!');
      loadSalas();
    } catch (error: unknown) {
      console.error('Erro ao excluir sala:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido ao excluir sala';
      toast.error(message);
    }
  };

  const resetForm = () => {
    reset({
      nome: '',
      descricao: '',
      capacidade: 1,
      equipamentos: '',
      imagem_url: '',
      is_active: true
    });
    setEditingSala(null);
  };

  const getEquipmentIcon = (equipment: string) => {
    const eq = equipment.toLowerCase();
    if (eq.includes('wifi') || eq.includes('internet')) return <Wifi className="w-4 h-4" />;
    if (eq.includes('projetor') || eq.includes('monitor') || eq.includes('tela')) return <Monitor className="w-4 h-4" />;
    if (eq.includes('café') || eq.includes('coffee')) return <Coffee className="w-4 h-4" />;
    if (eq.includes('estacionamento') || eq.includes('garagem')) return <Car className="w-4 h-4" />;
    return <Building className="w-4 h-4" />;
  };

  // Formulário responsivo para criar/editar sala
  const createFormFields = [
    {
      label: 'Nome da Sala',
      type: 'text' as const,
      value: '',
      onChange: (value: string) => setValue('nome', value),
      placeholder: 'Ex: Sala de Atendimento 1',
      required: true
    },
    {
      label: 'Capacidade',
      type: 'number' as const,
      value: '1',
      onChange: (value: string) => setValue('capacidade', parseInt(value) || 1),
      placeholder: 'Ex: 3',
      required: true
    },
    {
      label: 'Descrição',
      type: 'textarea' as const,
      value: '',
      onChange: (value: string) => setValue('descricao', value),
      placeholder: 'Ambiente silencioso e confortável para atendimento psicológico...',
      required: false
    },
    {
      label: 'Equipamentos (separados por vírgula)',
      type: 'text' as const,
      value: '',
      onChange: (value: string) => setValue('equipamentos', value),
      placeholder: 'Ex: Ar condicionado, Poltrona, Sofá',
      required: false
    },
    {
      label: 'URL da Imagem',
      type: 'url' as const,
      value: '',
      onChange: (value: string) => setValue('imagem_url', value),
      placeholder: 'https://exemplo.com/imagem.jpg',
      required: false
    }
  ];

  return (
    <ResponsiveAdminLayout
      title="Gestão de Salas"
      subtitle="Configure e gerencie as salas de atendimento"
    >
      {/* Search and Action Buttons */}
      <div className={`mb-6 flex ${isMobile ? 'flex-col gap-4' : 'items-center justify-between gap-4'}`}>
        <div className={`flex ${isMobile ? 'flex-col' : 'gap-4 items-center'} flex-1`}>
          <div className="flex-1 relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Buscar salas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <div className={`flex items-center space-x-2 ${isMobile ? 'mt-2' : ''}`}>
            <Switch
              id="show-active"
              checked={showActiveOnly}
              onCheckedChange={setShowActiveOnly}
            />
            <Label htmlFor="show-active">
              {showActiveOnly ? 'Apenas ativas' : 'Incluir inativas'}
            </Label>
          </div>
        </div>
        
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={resetForm} size={isMobile ? "sm" : "default"} className="bg-blue-600 hover:bg-blue-700 text-white">
              <Plus className="w-4 h-4 mr-2" />
              {isMobile ? 'Nova' : 'Nova Sala'}
            </Button>
          </DialogTrigger>
          
          <DialogContent className={`${isMobile ? 'max-w-[95vw] h-[90vh] overflow-y-auto' : 'max-w-2xl'}`}>
            <DialogHeader>
              <DialogTitle>Nova Sala</DialogTitle>
              <DialogDescription>
                Adicione uma nova sala para reservas
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className={`grid ${isMobile ? 'grid-cols-1' : 'grid-cols-2'} gap-4`}>
                <div>
                  <Label htmlFor="nome">Nome da Sala *</Label>
                  <TouchInput
                    id="nome"
                    {...register('nome')}
                    placeholder="Ex: Sala de Atendimento 1"
                  />
                  {errors.nome && (
                    <p className="text-sm text-red-600 mt-1">{errors.nome.message}</p>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="capacidade">Capacidade *</Label>
                  <TouchInput
                    id="capacidade"
                    type="number"
                    {...register('capacidade', { valueAsNumber: true })}
                    placeholder="Ex: 3"
                  />
                  {errors.capacidade && (
                    <p className="text-sm text-red-600 mt-1">{errors.capacidade.message}</p>
                  )}
                </div>
              </div>

              <div>
                <Label htmlFor="descricao">Descrição</Label>
                <Textarea
                  id="descricao"
                  {...register('descricao')}
                  placeholder="Ambiente silencioso e confortável para atendimento psicológico..."
                  rows={3}
                  className={isMobile ? 'min-h-[100px]' : ''}
                />
              </div>

              <div>
                <Label htmlFor="equipamentos">Equipamentos (separados por vírgula)</Label>
                <TouchInput
                  id="equipamentos"
                  {...register('equipamentos')}
                  placeholder="Ex: Ar condicionado, Poltrona, Sofá"
                />
              </div>

              <div>
                <Label htmlFor="imagem_url">URL da Imagem</Label>
                <TouchInput
                  id="imagem_url"
                  {...register('imagem_url')}
                  placeholder="https://exemplo.com/imagem.jpg"
                />
                {errors.imagem_url && (
                  <p className="text-sm text-red-600 mt-1">{errors.imagem_url.message}</p>
                )}
              </div>

              <div className="flex items-center space-x-2">
                <Controller
                  name="is_active"
                  control={control}
                  render={({ field }) => (
                    <Switch
                      id="is_active"
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  )}
                />
                <Label htmlFor="is_active">Sala ativa</Label>
              </div>

              <div className={`flex ${isMobile ? 'flex-col gap-2' : 'justify-end space-x-2'} pt-4`}>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setIsCreateDialogOpen(false);
                    resetForm();
                  }}
                  className={isMobile ? 'w-full' : ''}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  disabled={isSubmitting}
                  className={isMobile ? 'w-full' : ''}
                >
                  {isSubmitting ? 'Criando...' : 'Criar Sala'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Lista de Salas */}
      {loading ? (
        <div className="text-center py-8">
          <RefreshCw className="w-6 h-6 animate-spin mx-auto text-blue-500" />
          <p className="mt-2 text-gray-600">Carregando salas...</p>
        </div>
      ) : salas.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {salas.map((sala) => (
            <Card key={sala.id} className="flex flex-col">
              <CardHeader className="relative">
                {sala.imagem_url ? (
                  <img 
                    src={sala.imagem_url} 
                    alt={sala.nome} 
                    className="w-full h-40 object-cover rounded-t-lg" 
                  />
                ) : (
                  <div className="w-full h-40 bg-gray-200 rounded-t-lg flex items-center justify-center">
                    <Building className="w-12 h-12 text-gray-400" />
                  </div>
                )}
                <Badge 
                  className="absolute top-2 right-2"
                  variant={sala.is_active ? "default" : "destructive"}
                >
                  {sala.is_active ? 'Ativa' : 'Inativa'}
                </Badge>
              </CardHeader>
              <CardContent className="flex-grow">
                <CardTitle className="mb-2 text-lg">{sala.nome}</CardTitle>
                <p className="text-sm text-gray-600 mb-4">{sala.descricao}</p>
                
                <div className="flex items-center text-sm text-gray-700 mb-4">
                  <Users className="w-4 h-4 mr-2" />
                  <span>{sala.capacidade} pessoas</span>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-sm font-semibold mb-2">Equipamentos:</h4>
                  <div className="flex flex-wrap gap-2">
                    {sala.equipamentos.length > 0 ? sala.equipamentos.map(eq => (
                      <Badge key={eq} variant="secondary" className="flex items-center gap-1">
                        {getEquipmentIcon(eq)}
                        {eq}
                      </Badge>
                    )) : (
                      <p className="text-xs text-gray-500">Nenhum equipamento cadastrado</p>
                    )}
                  </div>
                </div>
              </CardContent>
              <div className="p-4 border-t flex items-center justify-between">
                <Button 
                  variant="outline"
                  onClick={() => setViewingSala(sala)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Ver Detalhes
                </Button>
                <div className="flex gap-2">
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="ghost" size="icon" onClick={() => handleEdit(sala)}>
                        <Edit className="w-4 h-4" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Editar Sala</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                        <div>
                          <Label htmlFor="edit-nome">Nome da Sala *</Label>
                          <TouchInput
                            id="edit-nome"
                            {...register('nome')}
                          />
                          {errors.nome && <p className="text-sm text-red-600 mt-1">{errors.nome.message}</p>}
                        </div>
                        <div>
                          <Label htmlFor="edit-capacidade">Capacidade *</Label>
                          <TouchInput
                            id="edit-capacidade"
                            type="number"
                            {...register('capacidade', { valueAsNumber: true })}
                          />
                          {errors.capacidade && <p className="text-sm text-red-600 mt-1">{errors.capacidade.message}</p>}
                        </div>
                        <div>
                          <Label htmlFor="edit-descricao">Descrição</Label>
                          <Textarea
                            id="edit-descricao"
                            {...register('descricao')}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-equipamentos">Equipamentos (separados por vírgula)</Label>
                          <TouchInput
                            id="edit-equipamentos"
                            {...register('equipamentos')}
                          />
                        </div>
                        <div>
                          <Label htmlFor="edit-imagem_url">URL da Imagem</Label>
                          <TouchInput
                            id="edit-imagem_url"
                            {...register('imagem_url')}
                          />
                           {errors.imagem_url && <p className="text-sm text-red-600 mt-1">{errors.imagem_url.message}</p>}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Controller
                            name="is_active"
                            control={control}
                            render={({ field }) => (
                              <Switch
                                id="edit-is_active"
                                checked={field.value}
                                onCheckedChange={field.onChange}
                              />
                            )}
                          />
                          <Label htmlFor="edit-is_active">Sala ativa</Label>
                        </div>
                        <div className="flex justify-end space-x-2 pt-4">
                          <Button type="button" variant="outline" onClick={() => setEditingSala(null)}>Cancelar</Button>
                          <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Salvando...' : 'Salvar Alterações'}
                          </Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                  
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Esta ação não pode ser desfeita. Isso irá "deletar" a sala, marcando-a como removida e escondendo-a da maioria das visualizações.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancelar</AlertDialogCancel>
                        <AlertDialogAction onClick={() => handleDelete(sala.id)}>
                          Sim, deletar sala
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Building className="w-12 h-12 mx-auto text-gray-400" />
          <h3 className="mt-2 text-lg font-medium">Nenhuma sala encontrada</h3>
          <p className="mt-1 text-sm text-gray-600">
            {searchTerm ? `Nenhuma sala encontrada para "${searchTerm}".` : 'Tente criar uma nova sala.'}
          </p>
        </div>
      )}

      {/* Modal de visualização */}
      <Dialog open={!!viewingSala} onOpenChange={() => setViewingSala(null)}>
        <DialogContent>
          {viewingSala && (
            <>
              <DialogHeader>
                <DialogTitle>{viewingSala.nome}</DialogTitle>
                <DialogDescription>{viewingSala.descricao}</DialogDescription>
              </DialogHeader>
              <div>
                {/* ... conteúdo do modal de visualização ... */}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </ResponsiveAdminLayout>
  );
};

export default AdminRooms; 